import { useEffect, useMemo, useRef } from 'react';
import { DrawerLayout } from 'components/DrawerLayout/DrawerLayout';
import HistoryTime from 'components/HistoryTime';
import {
  PersonalDataLabel,
  PropertyLabel,
} from 'constants/objectManager.constants';
import { useExtIdValidation } from 'hooks/useExtIdValidation';
import { useResponsiveDrawerColumns } from 'hooks/useResponsiveDrawerColumns';
import { castArray, debounce } from 'lodash';
import {
  selectObjectTypesList,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import {
  type ObjectTypeWithFilter,
  handleCombineObjectTypesWithFilter,
} from 'utils/objectTypes';
import type { TTEObject, TType } from '@timeedit/types';
import { EPersonalInformation, EProperty } from '@timeedit/types/lib/enums';
import { TEDrawerSection, TEInput, Typography } from '@timeedit/ui-components';
import { Form, Input, TreeSelect } from '@timeedit/ui-components/antd';
import type { ObjectManagerDetailProps } from '../ObjectManagerDetail';

type GeneralFormProps = Pick<
  TTEObject,
  'extId' | 'property' | 'personalInformation' | 'active' | 'types' | 'id'
>;

type TObjectTypeNode = {
  title: string;
  value: number;
  disabled?: boolean;
  children?: TObjectTypeNode[];
};

export const convertDataPopup = (
  tree: ObjectTypeWithFilter[]
): TObjectTypeNode[] => {
  return tree.map((item) => {
    const children = item.children
      ? convertDataPopup(item.children)
      : undefined;

    return {
      title: item.title,
      value: item.id,
      disabled: !(
        item.allowAbstractObjects ||
        item.allowStandardObjects ||
        item.allowVirtualObjects
      ),
      children,
    };
  });
};

const buildLabelMap = (
  nodes: TObjectTypeNode[],
  map = new Map<number, React.ReactNode>()
) => {
  for (const n of nodes) {
    map.set(n.value, n.title);
    if (n.children) buildLabelMap(n.children, map);
  }
  return map;
};

const GeneralInfo = ({ object, onChange }: ObjectManagerDetailProps) => {
  const { formLayout } = useResponsiveDrawerColumns();
  const [form] = Form.useForm<GeneralFormProps>();
  const { extIdValidationStatus, validateExtId } = useExtIdValidation(object);
  const objectTypesList: TType[] = useAppSelector(selectObjectTypesList);
  const objectTypesMap = useAppSelector(selectObjectTypesMap);

  const selectedTypes = Form.useWatch('types', form);

  const propertyOptions = useMemo(() => {
    const allTypeIds: number[] =
      selectedTypes == null ? [] : castArray(selectedTypes);

    const allTypes = allTypeIds
      .map((id) => objectTypesMap[id])
      .filter((type) => type != null);

    const allow = allTypes.reduce(
      (acc, t) => ({
        abstract: acc.abstract && t.allowAbstractObjects,
        virtual: acc.virtual && t.allowVirtualObjects,
        standard: acc.standard && t.allowStandardObjects,
      }),
      {
        abstract: true,
        virtual: true,
        standard: true,
      }
    );
    return [
      {
        value: EProperty.STANDARD,
        label: PropertyLabel[EProperty.STANDARD],
        disabled: !allow.standard,
      },
      {
        value: EProperty.ABSTRACT,
        label: PropertyLabel[EProperty.ABSTRACT],
        disabled: !allow.abstract,
      },
      {
        value: EProperty.VIRTUAL_STANDARD,
        label: PropertyLabel[EProperty.VIRTUAL_STANDARD],
        disabled: !allow.virtual,
      },
      {
        value: EProperty.VIRTUAL_ABSTRACT,
        label: PropertyLabel[EProperty.VIRTUAL_ABSTRACT],
        disabled: !allow.abstract || !allow.virtual,
      },
      {
        value: EProperty.TEMPLATE_OBJECT,
        label: PropertyLabel[EProperty.TEMPLATE_OBJECT],
        disabled: !allow.standard,
      },
    ];
  }, [selectedTypes, objectTypesMap]);

  // Stable debounced onChange via ref so it's not recreated on each render
  const onChangeDebounceRef = useRef<ReturnType<typeof debounce>>(null);
  if (!onChangeDebounceRef.current) {
    onChangeDebounceRef.current = debounce((partial: Partial<TTEObject>) => {
      onChange(partial);
    }, 300);
  }

  useEffect(() => {
    form.setFieldsValue({
      id: object.id,
      extId: object.extId,
      property: object.property,
      personalInformation: object.personalInformation,
      active: object.active,
      types: object.types,
    });
  }, [
    object.id,
    object.extId,
    object.property,
    object.personalInformation,
    object.active,
    object.types,
  ]);

  const treeData: TObjectTypeNode[] = useMemo(() => {
    const filtered = objectTypesList.filter((item) => item.extId !== 'root');
    const { dataFilter } = handleCombineObjectTypesWithFilter(filtered);
    return convertDataPopup(dataFilter);
  }, [objectTypesList]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      onChangeDebounceRef.current?.cancel();
    };
  }, []);
  const labelMap = useMemo(() => buildLabelMap(treeData), [treeData]);

  return (
    <TEDrawerSection label="General Info">
      <Form
        onValuesChange={(values, ...args) =>
          onChangeDebounceRef.current(values, ...args)
        }
        labelAlign="left"
        {...formLayout}
        form={form}
        validateTrigger={['onBlur']}
      >
        <DrawerLayout>
          <Form.Item
            style={{ marginBottom: '4px' }}
            label="Id"
            tooltip="Id"
            name="id"
            key="id"
            data-testid="id"
          >
            <Typography.Text data-testid="id-value">
              {String(form.getFieldValue('id'))}
            </Typography.Text>
          </Form.Item>
          <Form.Item
            style={{ marginBottom: '4px' }}
            label="Ext. Id"
            tooltip="Ext. Id"
            name="extId"
            key="extId"
            data-testid="ext-id"
            validateStatus={extIdValidationStatus}
            validateDebounce={500}
            {...(extIdValidationStatus !== undefined
              ? { hasFeedback: true }
              : {})}
            rules={[
              { required: true, message: 'Ext. Id is required!' },
              {
                async validator(_, value) {
                  return validateExtId(value);
                },
              },
            ]}
          >
            <Input id={extIdValidationStatus} />
          </Form.Item>
          <Form.Item
            style={{ marginBottom: '4px' }}
            label="Property"
            tooltip="Property"
            name="property"
            key="property"
            rules={[{ required: true, message: 'Property is required!' }]}
            data-testid="property"
          >
            <TEInput.Select options={propertyOptions} />
          </Form.Item>
          <Form.Item
            style={{ marginBottom: '4px' }}
            label="Personal Data"
            tooltip="Personal Data"
            name="personalInformation"
            key="personalInformation"
            data-testid="personal-data"
          >
            <TEInput.Select
              options={Object.entries(EPersonalInformation).map(
                ([label, value]) => ({
                  label: PersonalDataLabel[value],
                  value,
                })
              )}
            />
          </Form.Item>
          <Form.Item
            style={{ marginBottom: '4px' }}
            label="Type"
            tooltip="Type"
            name="types"
            key="types"
            data-testid="type"
            getValueFromEvent={(val) =>
              Array.isArray(val) ? val.map((v) => v.value) : val?.value
            }
            getValueProps={(ids?: number[] | number) => {
              if (ids == null) return {};
              const arr = Array.isArray(ids) ? ids : [ids];
              return {
                value: arr.map((id) => ({
                  value: id,
                  label: labelMap.get(id),
                })),
              };
            }}
            required
          >
            <TreeSelect
              showSearch
              multiple
              treeDefaultExpandAll
              treeData={treeData}
              placeholder="Please select types"
              treeCheckable
              treeNodeFilterProp="title"
              treeCheckStrictly
              popupMatchSelectWidth={false}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            style={{ marginBottom: '4px' }}
            label="Is Active"
            tooltip="Is Active"
            name="active"
            key="active"
            data-testid="is-active"
          >
            <TEInput.Switch size="small" />
          </Form.Item>
        </DrawerLayout>
      </Form>
      <HistoryTime
        history={object?.history}
        createdAt={object?.created}
        createdBy={object?.createdBy}
      />
    </TEDrawerSection>
  );
};

export default GeneralInfo;
